import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Mail, Check } from "lucide-react";
import { updateAssistant } from "../../actions/assistants";
import CardHeading from "./CardHeading";

/**
 * Where this assistant's emails go.
 *
 * `notificationEmail` is null until someone sets one, which means "use the
 * account email" - so an account with several assistants can send one site's
 * leads to sales@ and another's to the owner.
 *
 * Saved with PATCH /assistants/:id; an empty string clears it again. An
 * address the backend will not take comes back as a 400 with its own message.
 */
export default function NotificationEmailCard({ assistant, onSaved }) {
  const dispatch = useDispatch();
  // What it falls back to, so the field can say where mail goes today.
  const accountEmail = useSelector((state) => state.auth.user?.email);

  const saved = assistant?.notificationEmail || "";
  const [email, setEmail] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // The record arrives after the first paint, and can change when the
  // switcher moves to another assistant.
  useEffect(() => {
    setEmail(saved);
    setError("");
    setDone(false);
  }, [saved, assistant?.id]);

  const dirty = email.trim() !== saved;

  const save = async () => {
    setBusy(true);
    setError("");
    setDone(false);

    // Empty is a real value here: it clears the override.
    const result = await dispatch(
      updateAssistant(assistant.id, { notificationEmail: email.trim() }),
    );

    setBusy(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't save that address.");
      return;
    }

    setDone(true);
    onSaved?.();
  };

  return (
    <section className="dash-card">
      <CardHeading icon={Mail} tone="blue" title="Notification email" />

      <label className="ist-field">
        <span className="ist-field__label">Send this assistant&apos;s emails to</span>
        <span className="ist-field__input">
          <Mail size={16} strokeWidth={1.9} />
          <input
            type="email"
            value={email}
            placeholder={accountEmail || "you@example.com"}
            onChange={(e) => {
              setEmail(e.target.value);
              setDone(false);
            }}
            disabled={busy || !assistant?.id}
          />
        </span>
      </label>

      <p className="ist-card-subtitle">
        Emails about this assistant (new leads, handoff requests, new
        conversations) are sent here. Leave empty to use your account email
        {accountEmail ? `, ${accountEmail}` : ""}.
      </p>

      {error && <p className="auth-error">{error}</p>}

      <div className="ist-field__actions">
        <button
          type="button"
          className="onboarding__btn onboarding__btn--primary"
          onClick={save}
          disabled={busy || !dirty || !assistant?.id}
        >
          {busy ? (
            <span className="install-status-spinner" aria-hidden="true" />
          ) : null}
          {busy ? "Saving…" : "Save changes"}
        </button>

        {/* Only after a save, and only until the field is touched again. */}
        {done && !dirty && (
          <span className="ist-field__saved">
            <Check size={15} strokeWidth={2.6} />
            {saved ? "Saved" : "Using your account email"}
          </span>
        )}
      </div>
    </section>
  );
}
